import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "../loginSlice";
import { setBackButtonUrl } from "../uiSlice";
import Swal from "sweetalert2";
import ArrowButton from "../common/ArrowButton";
import { assetUrl, themeColors } from "../../functions/themeAssets";
import "../common/saveThem.css";

const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { status, user, error } = useSelector((state) => state.auth);
  const { status: themeStatus, data: themeData } = useSelector((state) => state.theme);

  const [searchParams] = useSearchParams();
  const isAdmin = searchParams.get("admin") === "true";
  const [landscape, setLandscape] = useState(false);

  useEffect(() => {
    dispatch(loginUser());
  }, [dispatch]);

  useEffect(() => {
    if (status === "succeeded" && user && isAdmin) {
      navigate("/admin");
    }
  }, [status, user, isAdmin, navigate]);

  useEffect(() => {
    if (status === "succeeded" && user?.backButtonRedirect) {
      dispatch(setBackButtonUrl(user.backButtonRedirect));
    }
  }, [status, user, dispatch]);

  useEffect(() => {
    if (status !== "failed") return;
    const msg = error ? String(error) : "Session expired";
    const redirect = user && user.backButtonRedirect ? user.backButtonRedirect : "/";
    Swal.fire("Session Expired!", msg, "error").then(() => {
      window.location.href = redirect;
    });
  }, [status, error, user]);

  // Players who already finished are sent back to the base game page, not the thank-you page
  const alreadyPlayed = Number(user?.gameover ?? 0) > 0;
  useEffect(() => {
    if (isAdmin || status !== "succeeded" || !user) return;
    if (alreadyPlayed) {
      Swal.fire({
        title: "Already Played",
        text: "You have already played this game.",
        icon: "info",
        confirmButtonText: "OK",
      }).then(() => {
        window.location.href = user.backButtonRedirect || process.env.REACT_APP_BASE_URL || "/";
      });
    }
  }, [status, user, isAdmin, alreadyPlayed]);

  // "BEGIN PLAY" moves on by itself after 5 seconds
  const canPlay = status === "succeeded" && !!user && !alreadyPlayed && !isAdmin;
  useEffect(() => {
    if (!canPlay) return;
    const t = setTimeout(() => navigate("/rules"), 5000);
    return () => clearTimeout(t);
  }, [canPlay, navigate]);

  // Ask phones to be held upright
  useEffect(() => {
    if (!isMobileDevice) return;
    const check = () => setLandscape(window.innerHeight < window.innerWidth);
    check();
    const id = setInterval(check, 1000);
    return () => clearInterval(id);
  }, []);

  if (status === "loading" || themeStatus === "loading") {
    return (
      <div className="st-loader">
        <div className="st-loader-spinner" />
      </div>
    );
  }

  const { box, text } = themeColors(themeData);

  return (
    <div className="st-login">
      {themeData?.logo && <img src={assetUrl(themeData.logo)} className="st-login-logo" alt="Game Logo" />}
      {canPlay && <ArrowButton to="/rules" label="BEGIN PLAY" box={box} text={text} />}
      {landscape && <div className="st-rotate">Please rotate your device for the best experience</div>}
    </div>
  );
}

export default Login;
